/**
 * /well-testing/ai/:id — one AI draft on its own page
 * (GET /well-testing/ai/digests/:id, fieldiq-api#1261). PF-6's draft page,
 * for this vertical. The back arrow returns to the register with its filters.
 *
 * Panels stack full width: the deterministic figures first (the API's, in
 * full), then the AI draft — "Draft — awaiting your approval" with Approve,
 * Edit-then-approve and Dismiss while the run is pending, the approved text
 * once a person has approved it. A run with no draft still shows its figures
 * and says the draft is unavailable, with the API's reason.
 */
import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../../components/ui/LoadFailureNotice';
import { useAuth } from '../../../contexts/AuthContext';
import { useWtAiDigest } from '../../../hooks/useWtAi';
import { useTenantTimeZone } from '../../../hooks/useTenantTimeZone';
import { formatInstant } from '../../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import { wtDay } from '../../../lib/well-testing';
import { canUseWtAi, wtAiAgentLabel, wtAiOutcomeSkin, wtAiScopeText } from '../../../lib/wt-ai';
import { useBackToList } from '../../downhole/recordPage';
import { isNotFound, WT_EYEBROW, WtBadge, WtLoading } from '../WtBits';
import { RunFigures, WtAiDraftPanel } from './WtAiBits';

const LIST = '/well-testing/ai';

export default function WtAiDraftDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const back = useBackToList(LIST);
  const { user } = useAuth();
  const allowed = canUseWtAi(user?.role);
  const tz = useTenantTimeZone();
  const query = useWtAiDigest(id, allowed);
  const run = query.data ?? null;
  const failure = queryLoadFailure(query);
  const notFound = !run && !query.isLoading && isNotFound(queryFailureError(query));
  const t = run?.triggerData ?? {};
  const ticketId = typeof t.ticketId === 'string' ? t.ticketId : null;
  const jobId = !ticketId && typeof t.jobId === 'string' ? t.jobId : null;

  return (
    <div data-testid="wt-ai-draft-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={`${WT_EYEBROW} · AI draft`}
        title={run ? wtAiAgentLabel(run.agent) : query.isLoading && allowed ? 'Loading…' : 'AI draft'}
        onBack={back}
        backLabel="Back to AI drafts"
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4" data-testid="wt-ai-draft-detail">
          {!allowed ? (
            <p className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-6 text-sm text-fieldiq-text" data-testid="wt-ai-not-allowed">
              AI drafts are for admins, office staff and supervisors.
            </p>
          ) : !run && query.isLoading ? (
            <WtLoading label="Loading the draft" />
          ) : notFound ? (
            <p className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-6 text-sm text-fieldiq-text" data-testid="wt-ai-not-found">
              This draft was not found.
            </p>
          ) : !run ? (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? undefined} subject="this draft" />
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <WtBadge skin={wtAiOutcomeSkin(run.outcome)} value={run.outcome} />
                <span className="text-fieldiq-text">{wtAiScopeText(String(run.agent), run.triggerData, wtDay)}</span>
                <span className="text-fieldiq-muted">Run {formatInstant(run.createdAt, tz) ?? '—'}</span>
                {run.reviewedAt && <span className="text-fieldiq-muted">· Reviewed {formatInstant(run.reviewedAt, tz) ?? '—'}</span>}
                {ticketId && (
                  <Link to={`/well-testing/tickets/${ticketId}`} className="font-bold text-fieldiq-text underline" data-testid="wt-ai-ticket-link">
                    Open the ticket
                  </Link>
                )}
                {jobId && (
                  <Link to={`/well-testing/jobs/${jobId}`} className="font-bold text-fieldiq-text underline" data-testid="wt-ai-job-link">
                    Open the job
                  </Link>
                )}
              </div>
              {run.outcome === 'superseded' && (
                <p role="status" className="text-xs text-fieldiq-muted">A newer run of this draft replaced this one, so it can no longer be approved.</p>
              )}
              <RunFigures agent={String(run.agent)} data={run.data} />
              <WtAiDraftPanel
                runId={run.agentRunId}
                outcome={run.outcome}
                draft={run.draft}
                approved={run.approved}
                approvedEdited={run.approvedEdited}
                aiError={run.aiError}
                canReview={allowed}
              />
            </>
          )}
        </div>
      </section>
    </div>
  );
}
